import { connect, injectReducer, injectSaga } from '@vfde-sails/core';
import * as avcActions from 'Container/AvailabilityCheck/action';
import * as appActions from 'Container/App/action';
import { setLmsOpener } from 'Container/LeadManagementForm/action';
import { selectors as mapStateToProps } from 'Container/AvailabilityCheck/selector';
import {
    AddressHtmlId,
    ResultHtmlId,
    OverlayContent,
    FormId,
    CheckModuleHtmlId,
} from '../../enums/elementId';
import { AVC_NAMESPACE } from 'Container/App/constants';
import saga from './saga';
import avcReducer from './reducer';
import { mountButtonLink } from '../../components/buttonLink';
import { getUpdateFlags } from './util/state';
import { updateFormElement, updateSelectFormElement } from '../../components/formElement';
import { mountLoadingSpinner, updateLoadingSpinner } from '../../components/loadingSpinner';
import {
    mountButton,
    updateButton,
    updateAvcResultButton,
    updateSubmitButtonState,
} from '../../components/button';
import { mountCaptcha } from '../../components/captcha';
import { mountNotification, updateAvcNotification } from '../../components/notification';
import {
    mountText,
    updateAddressText,
    updateAlternativeText,
} from '../../components/justText';
import { mountFormSublineTooltip } from '../../components/tooltip';

import { mountAvcForm } from './util/mountAvcForm';
import { LmsOpener } from 'Container/LeadManagementForm/interface';
import { isAddressError, isAddressNotFoundError } from './util/typeGuard';
import { addDisplayMetacodeToAvcResult } from '../../util/displayMetacodes';
import { mountHeadline, updateUpsellResultText } from '../../components/headline';
import { PreMarketingType } from '../../enums/product';
import { getPreMarketingResultUpsellBtnProps } from '../../util/components/buttonHelper';
import { toggleElementVisibilityById } from '@vfde-brix/ws10/core';
import { isPreSales } from './util/result';

const mapDispatch = {
    ...avcActions,
    ...appActions,
    setLmsOpener,
};

export type State = ReturnType<typeof mapStateToProps>;
export type Actions = typeof mapDispatch;

function container (state: State, actions: Actions) {
    injectSaga(AVC_NAMESPACE, saga);
    injectReducer(AVC_NAMESPACE, avcReducer);

    const avcForm = mountAvcForm(actions);
    const { city, street, number, select, submit } = avcForm;
    const spinner = mountLoadingSpinner(FormId.LOADING, true);
    const notification = mountNotification(ResultHtmlId.NOTIFICATION);
    const addressText = mountText(ResultHtmlId.CHECKED_ADDRESS);
    const alternativeText = mountText(ResultHtmlId.ALTERNATIVE_TEXT);
    const resultButton = mountButton(ResultHtmlId.BUTTON);
    const secondAlternativeText = mountHeadline(ResultHtmlId.SECOND_ALTERNATIVE_TEXT);
    const resultUpsellButton = mountButton(ResultHtmlId.ALTERNATIVE_BUTTON);

    mountFormSublineTooltip(FormId.TOOLTIP);
    mountButtonLink(AddressHtmlId.DELETE_ADDRESS, () => {
        actions.resetForm();
        actions.deleteAddress();
        actions.resetProviderToDefault();
        actions.resetOffersToPreVp();
    });
    mountButtonLink(CheckModuleHtmlId.BUTTON_LINK, () => {
        avcActions.setAccessSupplier('');
        actions.openOverlay(OverlayContent.FORM);
        actions.resetForm();
    });
    mountCaptcha();

    updateSubmitButtonState(submit, state.form);

    actions.getEntryState();

    return {
        getDerivedStateFromProps(newState: State, oldState: State) {
            const {
                address,
                addresses,
                error,
                avcResult,
                form,
                loading,
                selectedTariff,
                gigakombi,
                preMarketingType,
                availabilityIndicator,
                accessSupplier,
                rolloutEndMonth,
                rolloutStatus,
            } = newState;
            const update = getUpdateFlags(newState, oldState);
            update.button && updateSubmitButtonState(submit, form);
            update.loading && updateLoadingSpinner(spinner, loading);

            const fiberProduct = { availabilityIndicator, accessSupplier, rolloutEndMonth, rolloutStatus };

            if (update.addressNotification) {
                updateSelectFormElement(select, addresses);
                toggleElementVisibilityById(FormId.SELECT_CONTAINER, 1 < addresses.length);
            }

            if (update.form) {
                updateFormElement(city, form.city);
                updateFormElement(street, form.street);
                updateFormElement(number, form.number);
            }
            if (update.isValidAvcResult) {
                // Note: AVC result layer
                updateAvcNotification(notification, avcResult!, () => {
                    actions.setActiveOverlay(OverlayContent.LMS_MODULE);
                    actions.setLmsOpener(LmsOpener.RESULT_SCREEN);
                }, preMarketingType, fiberProduct);
                updateAddressText(addressText, avcResult!.address);
                updateAlternativeText(alternativeText, avcResult!, preMarketingType);

                updateAvcResultButton(
                    resultButton,
                    avcResult!.selected.id,
                    gigakombi,
                    avcResult!.fastest,
                    preMarketingType,
                );

                // Note: AVC result layer - Alternative offer for pre-marketing cases
                const altTariffForPreMarketing = avcResult?.upsell ?? avcResult?.fastest;
                const isAltOfferVisible = !!altTariffForPreMarketing
                    && (preMarketingType === PreMarketingType.PRE_FIBER || isPreSales(fiberProduct));

                toggleElementVisibilityById(
                    ResultHtmlId.ALTERNATIVE_WRAPPER,
                    isAltOfferVisible,
                );

                isAltOfferVisible && updateUpsellResultText(
                    secondAlternativeText,
                    altTariffForPreMarketing,
                );
                isAltOfferVisible && updateButton(
                    resultUpsellButton,
                    getPreMarketingResultUpsellBtnProps(altTariffForPreMarketing),
                );

                // Note: Custom display metacode
                addDisplayMetacodeToAvcResult(selectedTariff?.id || '');

                // Note: Address change button links
                const changeAddress = () => {
                    actions.openOverlay(OverlayContent.FORM);
                    actions.setAddresses([address!]);
                };
                mountButtonLink(AddressHtmlId.CHANGE_ADDRESS, changeAddress);
                mountButtonLink(ResultHtmlId.CHANGE_ADDRESS, changeAddress);
            }

            toggleElementVisibilityById(FormId.SERVICE_ERROR, Boolean(error && !isAddressError(error)));
            toggleElementVisibilityById(FormId.ADDRESS_ERROR, isAddressNotFoundError(error));
        },
    };
}

const Container = connect(mapStateToProps, mapDispatch)(container);

export default Container;
